import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  HostListener,
} from '@angular/core';
import { Router } from '@angular/router';
import { commonConstants } from '@telus/d2c-core/constants/common.constants';

import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

import { LoadingController, Platform } from '@ionic/angular';

import { Storage } from '@ionic/storage';
import {
  AvailableLangs,
  getBrowserLang,
  TranslocoService,
} from '@ngneat/transloco';
import { D2CUser, NotificationService } from '@telus/d2c-core';
import { AuthService } from '@telus/d2c-core/auth';
import { PageTitles } from '@telus/d2c-core/lib/common';
import { AlertService, menuPages } from '@telus/d2c-ui-common';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import throttle from 'lodash/throttle';
import { highlightedJSON } from '@telus/utils/utils';
import { D2CCentral } from '@telus/d2c-core';

const simulateClick = (elem: any) => {
  // Create our event (with options)
  const evt = new MouseEvent('click', {
    bubbles: true,
    cancelable: true,
    view: window,
  });
  // If cancelled, don't dispatch our event
  const canceled = !elem.dispatchEvent(evt);
};

@Component({
  selector: 'telus-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
  isAuthenticated: Observable<boolean>;
  canActivateProtectedRoutes: Observable<boolean>;
  appPages = [];
  d2cCentralPages = menuPages;
  loggedIn = false;
  availableLangs: AvailableLangs;
  isAlreadyEntering = false;
  public highlighterJson = highlightedJSON;

  private subscription: Subscription = Subscription.EMPTY;

  constructor(
    private platform: Platform,
    private router: Router,
    private storage: Storage,
    private transService: TranslocoService,
    private alertService: AlertService,
    private d2cCentral: D2CCentral,
    private authService: AuthService,
    public loadingController: LoadingController,
    private notificationService: NotificationService,
    private d2cUser: D2CUser
  ) {
    this.availableLangs = this.transService.getAvailableLangs();
    this.initializeApp();
    this.isAuthenticated = this.authService.isAuthenticated$;
    this.canActivateProtectedRoutes =
      this.authService.canActivateProtectedRoutes$;
  }

  get activeLang() {
    return this.transService.getActiveLang();
  }

  @HostListener('window:focus', ['$event'])
  onHostFocus(event: any) {
    if (
      !this.authService.hasValidToken() &&
      !this.router.url.includes(commonConstants.LOGIN)
    ) {
      this.showLogoutErrorMessage();
    }
  }

  async ngOnInit() {
    await this.storage.create();

    // checking if user info exist in localstorage
    const savedUserType = JSON.parse(localStorage.getItem('userType'));

    const isTelusMemberResult = this.d2cUser.isTelusMember();

    const userType = isTelusMemberResult ? 'TEAM' : 'PARTNER';

    // if save data is different than current data then we delete cache
    if (
      savedUserType?.userType !== userType &&
      savedUserType?.cpmsId !== this.d2cUser.info?.cpms
    ) {
      this.storage.clear();
    }
    const currentUser = {
      // if userType is TEAM then we set it to TEAM, if Partner then we set it to PARTNER. other else set empty
      userType: userType ? userType : '',
      cpmsId: this.d2cUser.info?.cpms,
    };
    localStorage.setItem('userType', JSON.stringify(currentUser));

    await this.checkLogin();
  }

  async checkLogin() {
    const thisRouter = this.router;
    const self = this;
    const loadingEl = await this.alertService.getLoadingCtrl(
      this.transService.translate('loading')
    );
    await loadingEl.present();

    this.isAuthenticated.subscribe({
      error: () => {
        self.dismissLoading(loadingEl);
      },
      complete: () => {},
      next: () => {
        const isLoggedIn = self.authService.hasValidToken();

        if (this.isAlreadyEntering) {
          return;
        }

        if (isLoggedIn) {
          this.isAlreadyEntering = true;

          this.authService.configurePrivileges().subscribe(
            (arePermissionsLoaded: boolean) => {
              if (
                arePermissionsLoaded &&
                this.router.url.indexOf(`/${commonConstants.LOGIN}`) !== -1
              ) {
                console.log('Case #1');
                const thisStorage = this.storage;

                // First check sessionStorage (where AuthGuard stores the URL)
                let previousStateFromSession = sessionStorage.getItem(
                  commonConstants.LAST_URL_BEFORE_LOGIN
                );

                if (previousStateFromSession) {
                  // Found URL in sessionStorage, use it and clear it
                  thisRouter
                    .navigateByUrl(previousStateFromSession)
                    .then(() => {
                      self.dismissLoading(loadingEl);
                    });
                } else {
                  // Fallback to Ionic Storage (for backward compatibility)
                  this.storage
                    .get(commonConstants.LAST_URL_BEFORE_LOGIN)
                    .then((previousState) => {
                      if (!previousState) {
                        previousState = `app/${PageTitles.vendor}`;
                      }

                      thisRouter.navigateByUrl(previousState).then(() => {
                        thisStorage.set(
                          commonConstants.LAST_URL_BEFORE_LOGIN,
                          ''
                        );
                        self.dismissLoading(loadingEl);
                      });
                    });
                }
              } else if (
                this.router.url.indexOf(`/${commonConstants.LOGIN}`) !== -1
              ) {
                console.log('Case #2');
                this.router
                  .navigateByUrl(`/${commonConstants.LOGIN}`)
                  .then(() => {
                    self.dismissLoading(loadingEl);
                  });
              } else {
                console.log('Case #3');
                // Check if there's a stored URL from a refresh scenario
                let previousStateFromSession = sessionStorage.getItem(
                  commonConstants.LAST_URL_BEFORE_LOGIN
                );

                if (
                  previousStateFromSession &&
                  this.router.url !== previousStateFromSession
                ) {
                  thisRouter
                    .navigateByUrl(previousStateFromSession)
                    .then(() => {
                      self.dismissLoading(loadingEl);
                    })
                    .catch((error) => {
                      console.error(
                        'AppComponent: Error restoring URL:',
                        error
                      );
                      self.dismissLoading(loadingEl);
                    });
                } else {
                  self.dismissLoading(loadingEl);
                }
              }
            },
            (e) => {
              console.log('Case #4');
              console.error('AppComponent: Error in configurePrivileges:', e);
              this.isAlreadyEntering = false;
              self.dismissLoading(loadingEl);
            }
          );
        } else if (this.router.url.indexOf(commonConstants.LOGIN) === -1) {
          console.log('Case #5');
          //this.router.navigateByUrl(`/${commonConstants.LOGIN}`).then(() => {
          //  self.dismissLoading(loadingEl);
          //});
          self.dismissLoading(loadingEl);
        } else {
          console.log('Case #6');
          self.dismissLoading(loadingEl);
        }
      },
    });
  }

  dismissLoading(loadingEl) {
    setTimeout(() => {
      loadingEl.dismiss();
    }, 0);
  }

  initializeApp() {
    this.platform.ready().then(() => {
      if (Capacitor.isPluginAvailable(commonConstants.SPLASH_SCREEN)) {
        SplashScreen.hide();
      }

      this.setInitialLanguage();
      this.d2cCentral.initialize();
    });
  }

  showLogoutErrorMessage() {
    /** DPD-1413: Closed existing session timeout message popup before display **/
    this.notificationService.clear();

    const message = this.transService.translate(`errors.sessionExpired`);
    const id = commonConstants.LOGIN_VIEW;

    const throttledLogoutNotification = throttle(() => {
      this.notificationService.showErrorMessage(
        this.transService.translate(`${id}.loggedOutMessage`),
        message ?? ''
      );
    }, 200);

    throttledLogoutNotification();
  }

  changeLanguage(lang: string) {
    // Ensure new active lang is loaded
    this.subscription.unsubscribe();
    this.subscription = this.transService
      .load(lang)
      .pipe(take(1))
      .subscribe(() => {
        this.transService.setActiveLang(lang);
      });
  }

  setInitialLanguage() {
    const browserLang = getBrowserLang();
    // This ts-ignore is on purpose and correct, because availableLanguages returns possible 2 types.
    // So TS just complain about uncertainty.
    // @ts-ignore
    const existingLang = this.availableLangs.find((lang) => {
      if (typeof lang === 'string') {
        return lang === browserLang;
      }
      return lang.id === browserLang;
    });

    if (existingLang !== undefined && this.activeLang !== browserLang) {
      this.changeLanguage(browserLang);
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  clear($event: any) {
    this.notificationService.showLogoutMessage$.next(false);
    // This is absolutely on purpose (sadly). I'm waiting for the stable 12 release of primeng so I can close an individual
    // message. Please check https://github.com/primefaces/primeng/pull/10057
    simulateClick(
      $event.target.parentNode.parentNode.parentNode.parentNode.parentNode.querySelector(
        'button.p-toast-icon-close'
      )
    );
  }
}
